import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BackendTag, TagRequest } from '@meta-press/shared-plugins';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { NotificationService } from '../notification-service/notification.service';
import { ConfirmModalComponent } from '../components/confirm-modal/confirm-modal.component';
import { AddTagModalComponent } from '../add-tag-modal/add-tag-modal.component';

@Component({
	selector: 'app-tag-management-modal',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './tag-management-modal.component.html',
	styleUrls: ['./tag-management-modal.component.scss'],
})
export class TagManagementModalComponent implements OnInit {
	editingTag: BackendTag | null = null;
	editTagForm: TagRequest = { Name: '', Description: '' };
	isEditingTag = false; // Loading state for editing tags
	sortColumn: 'name' | 'description' | 'usage' | null = 'name';
	sortDirection: 'asc' | 'desc' = 'asc';

	constructor(
		public activeModal: NgbActiveModal,
		protected sourceMaterialService: SourceMaterialService,
		private notificationService: NotificationService,
		private modalService: NgbModal,
	) {}

	ngOnInit(): void {
		// Load tags from SourceMaterialService
	}

	/**
	 * Open the add tag modal
	 */
	openAddTagModal(): void {
		this.modalService.open(AddTagModalComponent);
	}

	startEditTag(tag: BackendTag): void {
		this.editingTag = tag;
		this.editTagForm = {
			Name: tag.name,
			Description: tag.description || '',
		};
	}

	cancelEdit(): void {
		this.editingTag = null;
		this.editTagForm = { Name: '', Description: '' };
	}

	saveEditTag(): void {
		if (!this.editingTag) return;

		if (!this.editTagForm.Name.trim()) {
			this.notificationService.ShowToast(
				'Tagin nimi on pakollinen',
				'warning',
			);
			return;
		}

		// Check if name already exists (excluding current tag)
		const existingTag = this.sourceMaterialService.GetTagByName(
			this.editTagForm.Name.trim(),
		);
		if (existingTag && existingTag.id !== this.editingTag.id) {
			this.notificationService.ShowToast(
				'Tagi samalla nimellä on jo olemassa',
				'warning',
			);
			return;
		}

		this.isEditingTag = true;
		this.sourceMaterialService
			.UpdateTag(this.editingTag.id, {
				Name: this.editTagForm.Name.trim(),
				Description: this.editTagForm.Description.trim(),
			})
			.subscribe({
				next: () => {
					this.isEditingTag = false;
					this.notificationService.ShowToast(
						'Tagi päivitetty onnistuneesti',
						'success',
					);

					this.cancelEdit();
				},
				error: (error) => {
					this.isEditingTag = false;
					this.notificationService.ShowToast(
						'Tagin päivittäminen epäonnistui: ' + error,
						'danger',
					);
				},
			});
	}

	deleteTag(tag: BackendTag): void {
		const modalRef = this.modalService.open(ConfirmModalComponent);
		modalRef.componentInstance.title = 'Poista tagi';
		modalRef.componentInstance.message = `Haluatko varmasti poistaa tagin "${tag.name}"?`;
		modalRef.componentInstance.confirmButtonText = 'Poista';
		modalRef.componentInstance.confirmButtonClass = 'btn-danger';
		modalRef.componentInstance.manualClose = true;

		// Subscribe to confirmed event when manualClose is enabled
		modalRef.componentInstance.confirmed.subscribe(() => {
			// Enable loading state
			modalRef.componentInstance.loading = true;

			this.sourceMaterialService.DeleteTag(tag.id).subscribe({
				next: () => {
					// Close modal on success
					modalRef.close();

					this.notificationService.ShowToast(
						'Tagi poistettu onnistuneesti',
						'success',
					);

					// If we were editing this tag, cancel the edit
					if (this.editingTag && this.editingTag.id === tag.id) {
						this.cancelEdit();
					}
				},
				error: (error) => {
					// Close modal on error
					modalRef.close();

					this.notificationService.ShowToast(
						'Tagin poistaminen epäonnistui: ' + error,
						'danger',
					);
				},
			});
		});
	}

	/**
	 * Sort tags by a specific column
	 * @param column - The column to sort by
	 */
	sortTags(column: 'name' | 'description' | 'usage'): void {
		if (this.sortColumn === column) {
			// Toggle direction if clicking the same column
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Set new column and default to ascending
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}
	}

	/**
	 * Get the sort icon for a column
	 * @param column - The column to get the icon for
	 * @returns The Bootstrap icon class
	 */
	getSortIcon(column: 'name' | 'description' | 'usage'): string {
		if (this.sortColumn !== column) {
			return 'bi-arrow-down-up';
		}

		if (column === 'usage') {
			return this.sortDirection === 'asc'
				? 'bi-sort-numeric-down'
				: 'bi-sort-numeric-up';
		} else {
			return this.sortDirection === 'asc'
				? 'bi-sort-alpha-down'
				: 'bi-sort-alpha-up';
		}
	}

	/**
	 * Get tags sorted by the current sort column and direction
	 * @returns Array of tags sorted according to current settings
	 */
	get sortedTags(): BackendTag[] {
		const tags = [...this.sourceMaterialService.Tags];

		if (!this.sortColumn) {
			return tags;
		}

		return tags.sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (this.sortColumn) {
				case 'name':
					aValue = a.name.toLowerCase();
					bValue = b.name.toLowerCase();
					break;
				case 'description':
					aValue = (a.description || '').toLowerCase();
					bValue = (b.description || '').toLowerCase();
					break;
				case 'usage':
					aValue = this.getTagUsageCount(a.id);
					bValue = this.getTagUsageCount(b.id);
					break;
				default:
					return 0;
			}

			let comparison = 0;
			if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return this.sortDirection === 'asc' ? comparison : -comparison;
		});
	}

	/**
	 * Get the count of articles using a specific tag
	 * @param tagId - The ID of the tag to count usage for
	 * @returns The number of articles using this tag
	 */
	getTagUsageCount(tagId: number): number {
		return this.sourceMaterialService.Articles.filter((article) =>
			article.tags.includes(tagId),
		).length;
	}

	close(): void {
		this.activeModal.close();
	}
}
